<!--
  PlacementCompatibilityIndicator.vue -- Phase 4.2 fit verdict pill.

  Card: 2026-06-13 design bundle implementation --
  https://trello.com/c/hYlwyKYT
  Pebble: Phase 4.2 placement compatibility verdict + indicator.

  Renders a compact, color-coded verdict pill for one
  (artwork x placement zone) pair. Consumed by:

    * Phase 4.6 BulkApplySetModal mapping preview (one row per
      placement per product)
    * Phase 1 product editor bind step (reuse target -- single
      indicator next to the variant card)

  The verdict shape mirrors the backend ``compute_verdict()`` return
  contract:

      {
          verdict: "fit" | "remap" | "drop",
          reason: <human-readable string>,
          size_delta_pct: <number, percent>,
      }

  Per ADR 0046 + P4-Q14, this is **ThreadFlow local fit only**. A
  future ``method_feasibility`` row is reserved on the consuming card
  for the DecoNetwork verdict; this component is the size/zone fit
  half only.

  Visual treatment (from ADR 0046):

      fit   -- green check.   "Fits"
      remap -- amber arrows.  "Resize {n}%"
      drop  -- red minus.     "Wont fit"

  data-testid is per-verdict (``placement-compat-fit`` etc.) so UI
  scenarios and Vitest can assert on the rendered verdict without
  needing a wrapping classname.
-->
<template>
    <span
        class="placement-compat"
        :class="`placement-compat--${normalizedVerdict}`"
        :data-testid="`placement-compat-${normalizedVerdict}`"
        :title="reason"
        role="status"
        :aria-label="ariaLabel"
    >
        <span
            class="placement-compat__icon"
            aria-hidden="true"
        >{{ iconGlyph }}</span>
        <span class="placement-compat__label">{{ labelText }}</span>
    </span>
</template>

<script>
const VERDICT_FIT = 'fit'
const VERDICT_REMAP = 'remap'
const VERDICT_DROP = 'drop'

// "blocked" is a synthetic local verdict the consumer may pass when
// the row is tenant-blocked or otherwise non-applicable (see batch
// endpoint's null-verdict response slot). It renders as a gray
// neutral pill so the consumer doesn't need a separate component.
const VERDICT_BLOCKED = 'blocked'

const VALID_VERDICTS = new Set([
    VERDICT_FIT,
    VERDICT_REMAP,
    VERDICT_DROP,
    VERDICT_BLOCKED,
])

export default {
    name: 'PlacementCompatibilityIndicator',
    props: {
        /**
         * "fit" | "remap" | "drop" | "blocked" | null. ``null`` is
         * treated as ``blocked`` so the consumer can forward the
         * batch endpoint's null-verdict slot directly.
         */
        verdict: {
            type: String,
            default: null,
            validator(v) {
                return v === null || VALID_VERDICTS.has(v)
            },
        },
        /**
         * Human-readable explanation. Surfaces in the native tooltip
         * (``title``) and in the ``aria-label`` so screen readers
         * announce the full reason.
         */
        reason: {
            type: String,
            default: '',
        },
        /**
         * Percentage delta from source to remapped imprint dimensions.
         * Rendered as ``Resize {n}%`` in the remap pill. Optional --
         * the fit and drop pills do not surface it.
         */
        size_delta_pct: {
            type: Number,
            default: null,
        },
    },
    computed: {
        normalizedVerdict() {
            if (this.verdict === null || this.verdict === undefined) {
                return VERDICT_BLOCKED
            }
            return this.verdict
        },
        iconGlyph() {
            switch (this.normalizedVerdict) {
                case VERDICT_FIT:
                    // Heavy check.
                    return '✓'
                case VERDICT_REMAP:
                    // Resize / convertible double-arrow.
                    return '⇄'
                case VERDICT_DROP:
                    // Heavy minus.
                    return '−'
                case VERDICT_BLOCKED:
                default:
                    // Prohibited / no-entry style.
                    return '·'
            }
        },
        labelText() {
            switch (this.normalizedVerdict) {
                case VERDICT_FIT:
                    return 'Fits'
                case VERDICT_REMAP: {
                    const pct = this.size_delta_pct
                    if (pct === null || pct === undefined) {
                        return 'Resize'
                    }
                    // One decimal place; the backend math is float so
                    // we don't pretend more precision than exists.
                    return `Resize ${pct.toFixed(1)}%`
                }
                case VERDICT_DROP:
                    return "Won't fit"
                case VERDICT_BLOCKED:
                default:
                    return 'N/A'
            }
        },
        ariaLabel() {
            const base = `Placement compatibility: ${this.labelText}`
            if (this.reason) {
                return `${base}. ${this.reason}`
            }
            return base
        },
    },
}
</script>

<style scoped>
.placement-compat {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 12px;
    line-height: 1.4;
    font-weight: 500;
    border: 1px solid transparent;
    white-space: nowrap;
}

.placement-compat__icon {
    flex: 0 0 auto;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
}

.placement-compat__label {
    flex: 1 1 auto;
}

/* FIT -- green check. */
.placement-compat--fit {
    background: #ecfdf5;
    color: #065f46;
    border-color: #a7f3d0;
}
.placement-compat--fit .placement-compat__icon {
    background: #10b981;
    color: white;
}

/* REMAP -- amber arrows. Same family as the AtomicOrdersNotice copy
   panel so the surface vocabulary stays coherent. */
.placement-compat--remap {
    background: #fffbeb;
    color: #92400e;
    border-color: #fde68a;
}
.placement-compat--remap .placement-compat__icon {
    background: #f59e0b;
    color: white;
}

/* DROP -- red minus. */
.placement-compat--drop {
    background: #fef2f2;
    color: #991b1b;
    border-color: #fecaca;
}
.placement-compat--drop .placement-compat__icon {
    background: #ef4444;
    color: white;
}

/* BLOCKED / N/A -- gray neutral. Surfaces the batch endpoint's null
   verdict slot (tenant_blocked, unknown artwork, unknown zone). */
.placement-compat--blocked {
    background: #f3f4f6;
    color: #4b5563;
    border-color: #e5e7eb;
}
.placement-compat--blocked .placement-compat__icon {
    background: #9ca3af;
    color: white;
}
</style>
